import { useEffect, useState } from "react";
import NextImage from "next/legacy/image";

import styles from "./Image.module.css";

/**
 * Wraps next/image and degrades to a placeholder when the file will not load.
 *
 * Some album covers on the backend return HTTP 200 with `Content-Length: 0` —
 * a valid response carrying no image — which browsers render as their own
 * broken-image glyph. A 404 would do the same. Either way the page is better
 * off showing a deliberate stand-in than a broken icon.
 */
function Image({
  src,
  alt = "",
  width,
  height,
  layout,
  onError,
  onLoadingComplete,
  ...props
}) {
  const [failed, setFailed] = useState(false);

  // A new src deserves a fresh attempt — without this, one broken cover would
  // poison the slot for every image that reuses the component instance.
  useEffect(() => setFailed(false), [src]);

  const resolved =
    typeof src === "string" && src[0] === "/"
      ? process.env.NEXT_PUBLIC_BASE_URL + src
      : src;

  const handleError = (event) => {
    setFailed(true);
    if (onError) onError(event);
  };

  /**
   * `onError` alone is not enough. These pages are server-rendered, so the
   * browser starts loading the image from the markup and a broken one has
   * already failed by the time React hydrates and attaches the handler — the
   * event fires into nothing. next/image still reports the finished image
   * here, and a decoded width of zero is the reliable signal that it is
   * broken, whenever the failure happened.
   */
  const handleLoadingComplete = (result) => {
    if (!result?.naturalWidth) setFailed(true);
    if (onLoadingComplete) onLoadingComplete(result);
  };

  if (failed) {
    const isFill = layout === "fill";
    // Hold the image's shape rather than its exact pixels: the grid can be
    // narrower than the declared width, and a fixed height there would leave a
    // stretched box instead of a square sleeve.
    const ratio = `${width || 1} / ${height || 1}`;
    const sizing = isFill
      ? undefined
      : layout === "responsive"
        ? { width: "100%", aspectRatio: ratio }
        : { width, aspectRatio: ratio };

    return (
      <div
        className={`${styles.fallback} ${isFill ? styles.fill : ""}`}
        style={sizing}
        role="img"
        aria-label={alt || "Image unavailable"}
      >
        {/* A record, drawn rather than shipped, so it scales to any slot. */}
        <svg
          className={styles.icon}
          viewBox="0 0 48 48"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <circle cx="24" cy="24" r="21" />
          <circle cx="24" cy="24" r="12" />
          <circle cx="24" cy="24" r="3.2" fill="currentColor" stroke="none" />
        </svg>
        {alt ? <span className={styles.label}>{alt}</span> : null}
      </div>
    );
  }

  return (
    <NextImage
      src={resolved}
      alt={alt}
      width={width}
      height={height}
      layout={layout}
      onError={handleError}
      onLoadingComplete={handleLoadingComplete}
      {...props}
    />
  );
}

export default Image;
